/**
 * R24 — product selection toggle logic (pure, no React / DOM / WS).
 *
 * The single source of truth for "what happens when a product card is toggled": given the current
 * selection and a SKU, compute the next selection AND which tap message the server should hear
 * (`tap-to-select` on add / `tap-to-deselect` on remove — voice-client-contract.md §4).
 *
 * Extracted so BOTH tap targets on a card — the plus/check control AND the product image (R24:
 * "tapping the image selects too; the plus is sometimes not visible enough") — run ONE code path.
 * The component calls this once per user gesture, so exactly one message is emitted; there is no
 * second debounce window and no double-fire (the image and the plus are siblings, not nested, so a
 * tap on one never bubbles into the other — see ShowcasePanel).
 */

/** The two tap messages the server listens for (frozen, voice-client-contract.md §4). */
export type TapAction = 'tap-to-select' | 'tap-to-deselect';

export type SelectionToggle = {
  /** The next selection array (add on select, remove on deselect). */
  next: string[];
  /** Whether the SKU is selected AFTER the toggle. */
  selected: boolean;
  /** Which tap message the server should hear for this gesture. */
  action: TapAction;
};

/**
 * Compute the result of toggling `sku` against `current`.
 * - If `sku` is present, it is removed → `tap-to-deselect`.
 * - If `sku` is absent, it is appended → `tap-to-select`.
 * `current` is never mutated.
 */
export function toggleSelection(current: string[], sku: string): SelectionToggle {
  const isSelected = current.includes(sku);
  if (isSelected) {
    return {
      next: current.filter((s) => s !== sku),
      selected: false,
      action: 'tap-to-deselect',
    };
  }
  return {
    next: [...current, sku],
    selected: true,
    action: 'tap-to-select',
  };
}
